
import { useState, useMemo } from "react";
import { addDays, format, startOfWeek, isSameDay } from "date-fns";
import { it } from "date-fns/locale";
import { CalendarSettings } from "@/lib/supabase";

export const useCalendarNavigation = (
  currentDate: Date,
  onDateChange: (date: Date) => void,
  settings: CalendarSettings,
  activeTab: string,
  isMobile: boolean
) => {
  const startDate = useMemo(() => {
    return startOfWeek(currentDate, {
      weekStartsOn: 1
    });
  }, [currentDate]);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }).map((_, index) => {
      const day = addDays(startDate, index);
      return {
        date: day,
        dayName: format(day, "EEEE", { locale: it }),
        dayShort: format(day, "E", { locale: it }),
        dayNumber: format(day, "d", { locale: it }),
        isToday: isSameDay(day, new Date())
      };
    });
  }, [startDate]);

  const hours = useMemo(() => {
    if (!settings.startHour || !settings.endHour) {
      return [];
    }
    const { startHour, endHour } = settings;
    const result = [];
    for (let hour = startHour; hour <= endHour; hour++) {
      result.push(hour);
    }
    return result;
  }, [settings]);

  const goToPrevWeek = () => {
    onDateChange(addDays(startDate, -7));
  };

  const goToNextWeek = () => {
    onDateChange(addDays(startDate, 7));
  };

  const goToPrevDay = () => {
    onDateChange(addDays(currentDate, -1));
  };

  const goToNextDay = () => {
    onDateChange(addDays(currentDate, 1));
  };

  const visibleDays = useMemo(() => {
    // Mostra solo il giorno corrente in vista giornaliera
    if (activeTab === "day") {
      console.log("Usando vista giornaliera per:", currentDate);
      return weekDays.filter(day => isSameDay(day.date, currentDate));
    }
    
    // Filtra i giorni della settimana in base alle impostazioni sui giorni lavorativi
    return weekDays.filter((_, index) => !settings.workDays || settings.workDays.includes(index + 1));
  }, [weekDays, currentDate, settings.workDays, activeTab]);

  // Log di debug
  console.log("useCalendarNavigation:", {
    activeTab,
    isMobile,
    visibleDaysCount: visibleDays.length,
    date: currentDate.toISOString()
  });

  return {
    startDate,
    weekDays,
    hours,
    visibleDays,
    goToPrevWeek,
    goToNextWeek,
    goToPrevDay,
    goToNextDay
  };
};
